<!--
    NAVIGATION COMPONENT THAT IS RENDERED INSIDE OF ANALYSIS.VUE
    ALLOWS USER TO NAVIGATE BETWEEN THE MAIN COMPONENTS OF THE APP.
    COMPONENT PART OF © AREA 19, OCG WEB APP.
    DEVELOPED IN MONTREAL, QC, CANADA.
    JULY 2023.
  -->

<script>
import LoadSaveAnalysisStateDialog from '@src/components/LoadSaveAnalysisStateDialog.vue';
import { useActiveStudyStore } from '@src/store/active-study';

import { useAlert } from '@/src/composables/useAlert';
import { useAnalysisStore } from '@/src/store/analysis';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { useAppState } from '../store/ocg-app-state';

import NavBarButton from './NavBarButton.vue';

export default {
  components: {
    NavBarButton,
    LoadSaveAnalysisStateDialog,
  },
  data() {
    return {
      appState: useAppState(),
      activeStudyStore: useActiveStudyStore(),
      analysisStore: useAnalysisStore(),
      currentStudyStore: useCurrentStudyStore(),
    };
  },
  computed: {
    selectedTool() {
      return this.appState.currentView;
    },
  },
  created() {
    this.alert = useAlert();
  },

  methods: {
    goHome() {
      if (
        this.selectedTool === 'QualityCheck'
        && !this.activeStudyStore.hasSavedState
      ) {
        this.$refs.loadSaveAnalysisStateDialog
          ?.invokeSaveWarning(
            this.selectedTool,
            this.currentStudyStore.getStudyID,
          )
          .then((result) => {
            if (result) {
              this.$router.push('/home');
            }
          });
      }
      else {
        this.$router.push('/home');
      }
    },
    // Method to select a tool.
    selectTool(tool) {
      if (this.selectedTool === tool) {
        return; // If  already selected, do nothing
      }

      if (
        tool === 'GraphicalAnalysis'
        && !this.analysisStore.isStudyComplete()
      ) {
        const message = `Cannot proceed to ${tool}.
           Quality Check step is not complete yet. Ensure the segmentation masks and the biomarkers are properly accquired before continuing`;
        this.alert.warning(message);
        return;
      }
      const { hasSavedState } = this.activeStudyStore;
      // Switched warning to auto-save when navigating between modules.
      if (!hasSavedState) {
        this.$refs.loadSaveAnalysisStateDialog
          ?.backgroundAutosave(this.currentStudyStore.getStudyID)
          .then(() => {
            this.alert.info(
              'Your analysis state has been autosaved in the background.',
            );
            this.appState.setView(tool);
          });
        // this.$refs.loadSaveAnalysisStateDialog
        //   ?.invokeSaveWarning(
        //     this.selectedTool,
        //     this.currentStudyStore.getStudyID
        //   )
        //   .then((result) => {
        //     if (result) {
        //       this.appState.setView(tool);
        //     }
        //   });
        return;
      }
      this.appState.setView(tool);
    },

    /**
     * Handles keydown events and performs actions based on the pressed key.
     *
     * @param {Event} event - The keydown event object.
     */
    handleKeyDown(event) {
      // Define a mapping of key presses to tool names
      const keyToToolMap = {
        1: 'QualityCheck',
        2: 'GraphicalAnalysis',
        3: 'ViewerComponent',
        4: 'FunctionComponent',
        5: 'FlowComponent',
        6: 'ReportMaker',
        Escape: '/home',
      };

      // Check if the pressed key is in the mapping and select the corresponding tool or route
      const selectedTool = keyToToolMap[event.key];

      if (selectedTool) {
        if (event.key === 'Escape') {
          this.$router.push(selectedTool);
        }
        else {
          this.selectTool(selectedTool);
        }
      }
    },
  },
};
</script>

<template>
  <!-- Tools section -->
  <div
    class="simple-panel flex flex-column justify-center items-center py-4 px-2 gap-4"
  >
    <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
    <!-- <a href="/home" style="text-decoration: none"> -->
    <NavBarButton
      img-source="Patient List.svg"
      tag="Patient List"
      @click.prevent="goHome"
    />
    <!-- </a> -->

    <!-- Contour Check Button -->

    <NavBarButton
      :img-source="
        selectedTool === 'QualityCheck'
          ? 'CheckContours_Active.svg'
          : 'CheckContours.svg'
      "
      tag="Quality Check"
      :active="selectedTool === 'QualityCheck'"
      @click="selectTool('QualityCheck')"
    />

    <!-- Graphical Analysis Button -->

    <NavBarButton
      :img-source="
        selectedTool === 'GraphicalAnalysis'
          ? 'Analysis_Active.svg'
          : 'Analysis.svg'
      "
      tag="Analysis"
      :active="selectedTool === 'GraphicalAnalysis'"
      @click="selectTool('GraphicalAnalysis')"
    />

    <NavBarButton
      img-source="ViewerComponent.svg"
      tag="Viewer"
      :active="selectedTool === 'ViewerComponent'"
      @click="selectTool('ViewerComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'FunctionComponent'
          ? 'FunctionComponent_Active.svg'
          : 'FunctionComponent.svg'
      "
      tag="Function"
      :active="selectedTool === 'FunctionComponent'"
      @click="selectTool('FunctionComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'FlowComponent'
          ? 'FlowComponent_Active.svg'
          : 'FlowComponent.svg'
      "
      tag="Flow"
      :active="selectedTool === 'FlowComponent'"
      @click="selectTool('FlowComponent')"
    />

    <NavBarButton
      :img-source="
        selectedTool === 'ReportMaker' ? 'Report_Active.svg' : 'Report.svg'
      "
      tag="Report"
      :active="selectedTool === 'ReportMaker'"
      @click="selectTool('ReportMaker')"
    />
  </div>

  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />
</template>

<style scoped>
/* Styles for tools section */
.tools {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 60px;
}

/* Styles for active tool */
.active {
  color: #00ddff;
}

/* Styles for each individual tool */
.tool {
  justify-items: center;
  cursor: pointer;
}

/* Hover styles for each individual tool */
.tool:hover {
  color: #00ddffea;
  transition: 0.3s;
}

/* Styles for h4 element (section label) */
.section-tag {
  font-size: 12px;
  text-align: center;
}
</style>
